import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Header } from '../components/landing/Header';
import { HeroSection } from '../components/landing/HeroSection';
import { TrustStrip } from '../components/landing/TrustStrip';
import { FeatureSuite } from '../components/landing/FeatureSuite';
import { AudienceCards } from '../components/landing/AudienceCards';
import { HowItWorks } from '../components/landing/HowItWorks';
import { LeadGenForm } from '../components/landing/LeadGenForm';
import { FAQ } from '../components/landing/FAQ';
import { Footer } from '../components/landing/Footer';
import { InstallAppModal } from '../components/landing/InstallAppModal';
import { getAuthToken, getStoredUser } from '../services/auth';
import {
  initLandingAnalytics,
  teardownLandingAnalytics,
  trackInstallAttempt,
} from '../services/landingAnalytics';

const dashboardForRole = (role) => {
  switch (role) {
    case 'resident':
      return '/resident';
    case 'management':
      return '/management';
    case 'guard':
      return '/guard';
    case 'super_admin':
      return '/super-admin';
    default:
      return null;
  }
};

export default function LandingPage() {
  const [installOpen, setInstallOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Auto-redirect logged-in users to their dashboard
  const token = getAuthToken();
  const user = getStoredUser();
  const dest = token && user ? dashboardForRole(user.role) : null;

  // If the page is opened from an installed PWA (display-mode: standalone),
  // skip the marketing landing entirely and go to /login (or dashboard if logged in).
  // start_url in manifest.json is also /login but this is a defensive runtime check
  // for users whose cached manifest still points to /.
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => {
      setInstallOpen(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  // Initialize anonymous analytics for this landing visit (only when the
  // visitor is unauthenticated — logged-in users get redirected away anyway).
  useEffect(() => {
    if (dest) return;
    initLandingAnalytics();
    return () => teardownLandingAnalytics();
  }, [dest]);

  const openInstall = (source) => {
    trackInstallAttempt(source);
    setInstallOpen(true);
  };

  if (dest) {
    return <Navigate to={dest} replace />;
  }
  if (isStandalone) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900 antialiased"
      style={{ fontFamily: 'Work Sans, sans-serif' }}
      data-testid="landing-page"
    >
      <Header onInstallClick={() => openInstall('header')} />
      <main>
        <HeroSection onInstallClick={() => openInstall('hero')} />
        <TrustStrip />
        <FeatureSuite />
        <AudienceCards />
        <HowItWorks />
        <LeadGenForm />
        <FAQ />
      </main>
      <Footer />

      <InstallAppModal
        open={installOpen}
        onOpenChange={setInstallOpen}
        deferredPrompt={deferredPrompt}
        onInstalled={() => setInstallOpen(false)}
      />
    </div>
  );
}
